<script setup lang="ts">
import type { AIProviderName } from '@wilson/shared'
import { onMounted, ref } from 'vue'
import { api, jsonBody } from '../../services/api'

interface ServerConfig {
  id: string
  name: string
  provider: AIProviderName
  baseUrl: string
  model: string | null
  isActive: boolean
  hasApiKey: boolean
}
const configs = ref<ServerConfig[]>([])
const form = ref<{
  name: string
  provider: AIProviderName
  baseUrl: string
  model: string
  apiKey: string
  isActive: boolean
}>({
  name: 'Local Ollama',
  provider: 'ollama',
  baseUrl: 'http://ollama:11434',
  model: '',
  apiKey: '',
  isActive: true,
})
const status = ref('')
const error = ref('')
async function load() {
  const data = await api<{ configs: ServerConfig[] }>('/admin/settings/ai')
  configs.value = data.configs
}
async function test() {
  try {
    const result = await api<{ ok: boolean }>('/admin/settings/ai/test', {
      method: 'POST',
      ...jsonBody(form.value),
    })
    status.value = result.ok ? 'Connection successful.' : 'The provider did not respond.'
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Connection test failed.'
  }
}
async function discover() {
  try {
    const result = await api<{ models: Array<{ id: string }> }>('/admin/settings/ai/models', {
      method: 'POST',
      ...jsonBody(form.value),
    })
    status.value = result.models.length
      ? `Available: ${result.models.map(({ id }) => id).join(', ')}`
      : 'Connected, but no models were returned.'
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Models could not be loaded.'
  }
}
async function save() {
  try {
    await api('/admin/settings/ai', { method: 'POST', ...jsonBody(form.value) })
    status.value = 'Server provider saved with encrypted credentials.'
    form.value.apiKey = ''
    await load()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Provider could not be saved.'
  }
}
onMounted(load)
</script>

<template>
  <header class="admin-heading">
    <div>
      <p class="eyebrow">Provider-agnostic configuration</p>
      <h1>AI settings</h1>
      <p>
        Server credentials are encrypted. Browser credentials never leave the current browser
        session except when calling the chosen provider.
      </p>
    </div>
  </header>
  <p v-if="error" class="form-error banner">{{ error }}</p>
  <p v-if="status" class="notice">{{ status }}</p>
  <div class="admin-grid">
    <section class="admin-card editor-form">
      <div class="card-heading"><h2>Add server provider</h2></div>
      <div class="form-grid">
        <label>Name<input v-model="form.name" /></label
        ><label
          >Provider<select v-model="form.provider">
            <option value="ollama">Ollama</option>
            <option value="openai-compatible">OpenAI-compatible</option>
          </select></label
        ><label class="span-2">Base URL<input v-model="form.baseUrl" type="url" /></label
        ><label>Default model<input v-model="form.model" /></label
        ><label
          >API key <span class="optional">optional</span
          ><input v-model="form.apiKey" type="password"
        /></label>
      </div>
      <div class="button-row">
        <button class="button secondary" @click="test">Test connection</button
        ><button class="button secondary" @click="discover">List models</button
        ><button class="button primary" @click="save">Save provider</button>
      </div>
    </section>
    <section class="admin-card">
      <div class="card-heading"><h2>Saved providers</h2></div>
      <ul class="provider-list">
        <li v-for="config in configs" :key="config.id">
          <div>
            <strong>{{ config.name }}</strong
            ><small>{{ config.provider }} · {{ config.baseUrl }}</small>
          </div>
          <div>
            <span class="status-pill" :class="config.isActive ? 'active' : 'retired'">{{
              config.isActive ? 'Active' : 'Inactive'
            }}</span
            ><small>{{ config.hasApiKey ? 'Encrypted key stored' : 'No key' }}</small>
          </div>
        </li>
      </ul>
      <p v-if="!configs.length" class="empty-copy">
        No server providers saved. Browser AI still works independently.
      </p>
    </section>
  </div>
</template>
